import { NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { Condition, ICondition } from "../contracts/policies/condition";
import Link from "../link";
import { ConditionResource } from "../models/policies/condition-resource";

export class ConditionMapper {
    /**
     * Map condition resources to conditions.
     * @param client - An instance of an extended Content Hub client
     * @param conditionResources - A collection of resources to map
     * @returns An array of conditions.
     */
    static async mapToConditionsAsync(
        client: IExtendedContentHubClient,
        conditionResources: Array<ConditionResource>
    ): Promise<Array<ICondition>> {
        if (conditionResources == null || conditionResources.length === 0) {
            return [];
        }

        const list: Array<ICondition> = [];
        for (const conditionResource of conditionResources) {
            const condition = await this.mapToConditionAsync(client, conditionResource);
            if (condition != null) {
                list.push(condition);
            }
        }

        return list;
    }

    /**
     * Map a condition resource to a condition instance.
     * @param client - An instance of an extended Content Hub client
     * @param conditionResource - A condition resource
     * @returns A condition or null.
     */
    static async mapToConditionAsync(
        client: IExtendedContentHubClient,
        conditionResource: ConditionResource
    ): NullableResultPromise<ICondition> {
        if (conditionResource == null) {
            return null;
        }

        const condition: ICondition = new Condition();
        condition.directEntityIds = await this.mapIdAsync(client, conditionResource.directEntities);
        condition.entityIds = await this.mapIdAsync(client, conditionResource.entities);

        return condition;
    }

    /**
     * Map condition instances to condition resources.
     * @param client - An instance of an extended Content Hub client
     * @param conditions - A collection of conditions
     * @returns An array of condition resources.
     */
    static async mapToConditionResourcesAsync(
        client: IExtendedContentHubClient,
        conditions: Array<ICondition>
    ): Promise<Array<ConditionResource>> {
        if (conditions == null || conditions.length === 0) {
            return [];
        }

        const resources: Array<ConditionResource> = [];
        for (const condition of conditions) {
            const conditionResource = await this.mapToConditionResourceAsync(client, condition);
            if (conditionResource != null) {
                resources.push(conditionResource);
            }
        }

        return resources;
    }

    /**
     * Map a condition to a condition resource.
     * @param client - An instance of an extended Content Hub client
     * @param condition - A condition instance
     * @returns A condition resource or null.
     */
    static async mapToConditionResourceAsync(
        client: IExtendedContentHubClient,
        condition: ICondition
    ): NullableResultPromise<ConditionResource> {
        if (condition == null) {
            return null;
        }

        const conditionResource = new ConditionResource();
        conditionResource.directEntities = await this.mapEntityLinksAsync(client, condition.directEntityIds);
        conditionResource.entities = await this.mapEntityLinksAsync(client, condition.entityIds);

        return conditionResource;
    }

    //#region Private methods

    private static async mapIdAsync(client: IExtendedContentHubClient, idLinks: Array<Link>): Promise<Array<number>> {
        if (idLinks == null) {
            return [];
        }

        const list = await client.linkHelper.idsFromEntitiesAsync(idLinks);
        return list;
    }

    private static async mapEntityLinksAsync(
        client: IExtendedContentHubClient,
        entityLinks: Array<number>
    ): Promise<Array<Link>> {
        if (entityLinks == null) {
            return [];
        }

        const list = await client.linkHelper.entitiesToLinksAsync(entityLinks);
        return list;
    }

    //#endregion
}
